import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Translation } from '@ps/web-shared-components';
import { compareWithNatNumber, minLength, numbersOnly, required } from '@ps/web-shared-validators';
import React from 'react';
import type { FieldMetaState } from 'react-final-form';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import { CardType } from '../../../../../../types/cardType';
import type { Validator } from '../../../../../AccountTypeToggleGroup/AccountTypeToggleGroup';
import AccountTypeToggleGroup from '../../../../../AccountTypeToggleGroup/AccountTypeToggleGroup';
import BankFields from '../../../../../BankFields/BankFields';
import Phone from '../../../../../Phone/Phone';
import TextInput from '../../../../../TextInput/TextInput';

import {
  TXTCLI_Social_Insurance_Number,
  TXTWCL_Insert_last_4_digits_of_your_S_ELL,
  TXTWCL_Please_enter_N0_digit_number,
  TXTWCL_Please_enter_the_phone_number__ELL,
  TXTWCL_SSN,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL
} from './ECheck.messages.json';

const fieldName = 'deposit';

export const getCommonFields = ({
  fieldName,
  meta,
  validators,
  cardType
}: {
  fieldName: string;
  meta: FieldMetaState<string>;
  validators?: Validator;
  cardType: CardType;
}) => (
  <>
    <AccountTypeToggleGroup fieldName={fieldName} validators={validators} meta={meta} />
    <BankFields fieldName={fieldName} validators={validators} cardType={cardType} />
  </>
);

const ECheck: React.FC = () => {
  const phone = useSelector(select.account.getPhone);
  const natNumber = useSelector(select.account.getNationalDocumentNumber);
  const { meta } = useField(`${fieldName}.accountType`);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const site = useSelector(select.cms.getSiteId);

  return (
    <>
      {getCommonFields({ fieldName, meta, cardType: CardType.ECheck })}
      {(site === Sites.CA_ON || site === Sites.COM) && (
        <>
          <TextInput
            id={`${fieldName}.ssn`}
            type="tel"
            label={TXTCLI_Social_Insurance_Number}
            placeholder="000000000"
            maxLength={9}
            validators={[
              required({ ...TXTWCL_Please_enter_N0_digit_number, values: { 0: 9 } }),
              numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL),
              minLength(9, { ...TXTWCL_Please_enter_N0_digit_number, values: { 0: 9 } })
            ]}
          />
          <Phone
            id={`${fieldName}.phone`}
            maxLength={15}
            value={phone}
            helperMessage={<Translation {...TXTWCL_Please_enter_the_phone_number__ELL} />}
          />
        </>
      )}
      {isSiteUS && (
        <TextInput
          id={`${fieldName}.ssn`}
          type="tel"
          label={TXTWCL_SSN}
          placeholder="0000"
          helperMessage={<Translation {...TXTWCL_Insert_last_4_digits_of_your_S_ELL} />}
          maxLength={4}
          validators={[
            required({ ...TXTWCL_Please_enter_N0_digit_number, values: { 0: 4 } }),
            numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL),
            minLength(4, { ...TXTWCL_Please_enter_N0_digit_number, values: { 0: 4 } }),
            (value) => compareWithNatNumber(value, natNumber)
          ]}
        />
      )}
    </>
  );
};

export default ECheck;
